import { MarkerF, PolylineF } from '@react-google-maps/api';
import { memo, useCallback, useMemo } from 'react';
import type { NativeMapMarker, NativeMapRoute } from './NativeMapSurface';

type RouteProps = { route: NativeMapRoute };
type MarkerProps = { marker: NativeMapMarker; onMarkerClick?: (id: string) => void };

/** Geometry descriptors are immutable; a changed path has a new array. */
export function sameWebMapRouteProps(previous: RouteProps, next: RouteProps) {
  const left = previous.route;
  const right = next.route;
  return left === right || left.id === right.id && left.path === right.path
    && left.color === right.color && left.opacity === right.opacity
    && left.weight === right.weight && left.zIndex === right.zIndex && left.dashed === right.dashed;
}

export function sameWebMapMarkerProps(previous: MarkerProps, next: MarkerProps) {
  if (previous.onMarkerClick !== next.onMarkerClick) return false;
  const left = previous.marker;
  const right = next.marker;
  return left === right || left.id === right.id
    && left.coordinate.lat === right.coordinate.lat && left.coordinate.lng === right.coordinate.lng
    && left.title === right.title && left.label === right.label && left.labelClassName === right.labelClassName && left.labelColor === right.labelColor
    && left.iconUrl === right.iconUrl && left.iconWidth === right.iconWidth && left.iconHeight === right.iconHeight
    && left.anchorX === right.anchorX && left.anchorY === right.anchorY
    && left.interactive === right.interactive && left.zIndex === right.zIndex;
}

export function webMapRouteOptions(route: Pick<NativeMapRoute, 'color' | 'opacity' | 'weight' | 'zIndex' | 'dashed'>): google.maps.PolylineOptions {
  return {
    strokeColor: route.color, strokeOpacity: route.dashed ? 0 : route.opacity ?? .92,
    strokeWeight: route.weight ?? 5, zIndex: route.zIndex ?? 3, clickable: false,
    icons: route.dashed ? [{ icon: { path: 'M 0,-1 0,1', strokeColor: route.color, strokeOpacity: route.opacity ?? .92, strokeWeight: 3, scale: 2 }, offset: '0', repeat: '12px' }] : [],
  };
}

/** Advancing a trace updates its path, not its unchanged Google style options. */
export const WebMapRoute = memo(function WebMapRoute({ route }: RouteProps) {
  const { color, opacity, weight, zIndex, dashed } = route;
  const options = useMemo(() => webMapRouteOptions({ color, opacity, weight, zIndex, dashed }), [color, opacity, weight, zIndex, dashed]);
  return <PolylineF path={route.path} options={options} />;
}, sameWebMapRouteProps);

/** Static pins stay untouched during playback; a moving cursor only moves. */
export const WebMapMarker = memo(function WebMapMarker({ marker, onMarkerClick }: MarkerProps) {
  const { id, coordinate, title, label: text, labelClassName, labelColor, iconUrl, iconWidth = 40, iconHeight = 44, anchorX, anchorY, interactive, zIndex } = marker;
  const isNameLabel = labelClassName === 'map-pin-name-label';
  const icon = useMemo<google.maps.Icon | undefined>(() => iconUrl ? {
    url: iconUrl,
    scaledSize: new google.maps.Size(iconWidth, iconHeight),
    anchor: new google.maps.Point(anchorX ?? iconWidth / 2, anchorY ?? iconHeight),
    labelOrigin: new google.maps.Point(iconWidth / 2, isNameLabel ? iconHeight + 10 : iconHeight / 2),
  } : undefined, [iconUrl, iconWidth, iconHeight, anchorX, anchorY, isNameLabel]);
  const label = useMemo<google.maps.MarkerLabel | undefined>(() => text ? {
    text, color: labelColor ?? (isNameLabel ? '#171717' : '#ffffff'), fontWeight: '600', fontSize: '12px', className: labelClassName,
  } : undefined, [text, isNameLabel, labelClassName, labelColor]);
  // MarkerF does not clear an old label when its label prop becomes undefined.
  const options = useMemo<google.maps.MarkerOptions>(() => ({ clickable: interactive !== false, ...(label ? {} : { label: null }) }), [interactive, label]);
  const onClick = useCallback(() => { if (interactive !== false) onMarkerClick?.(id); }, [id, interactive, onMarkerClick]);
  return <MarkerF position={coordinate} title={title} zIndex={zIndex} icon={icon} label={label} options={options} onClick={onClick} />;
}, sameWebMapMarkerProps);
